import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

type L = { label: string; to: string };
const cls = "group inline-flex items-stretch border border-neutral-900 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-900 hover:text-white transition-colors max-w-[48%]";

function Btn({ l, dir, testId, state }: { l: L; dir: -1 | 1; testId: string; state?: any }) {
  const icon = <span className={`flex items-center px-3 ${dir < 0 ? "border-r" : "border-l"} border-neutral-900 group-hover:border-white/30`}>{dir < 0 ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}</span>;
  const body = <>{dir < 0 && icon}<span className="px-5 py-3.5 truncate">{l.label}</span>{dir > 0 && icon}</>;
  if (l.to.startsWith("#")) return <a href={l.to} className={cls} data-testid={testId} aria-label={`${dir < 0 ? "Previous" : "Next"}: ${l.label}`}>{body}</a>;
  return <Link to={l.to} state={state} onClick={() => window.scrollTo(0, 0)} className={cls} data-testid={testId} aria-label={`${dir < 0 ? "Previous" : "Next"}: ${l.label}`}>{body}</Link>;
}

export function StepNav({ prev, next, testId, state }: { prev?: L | null; next?: L | null; testId: string; state?: any }) {
  if (!prev && !next) return null;
  return (
    <nav className="max-w-[1400px] mx-auto px-5 md:px-12 py-8 flex justify-between gap-4" data-testid={testId}>
      {prev ? <Btn l={prev} dir={-1} testId={`${testId}-prev`} state={state} /> : <span />}
      {next ? <Btn l={next} dir={1} testId={`${testId}-next`} state={state} /> : <span />}
    </nav>
  );
}

export function TabsStepNav({ tabs }: { tabs: any }) {
  const { pathname } = useLocation();
  const items = (tabs?.items || []).filter((x: any) => x.label && x.url);
  const i = items.findIndex((x: any) => x.url === pathname || (x.also || []).includes(pathname));
  if (i < 0) return null;
  const at = (j: number) => (items[j] ? { label: items[j].label, to: items[j].url } : null);
  return <StepNav prev={at(i - 1)} next={at(i + 1)} testId="page-step-nav" />;
}

const clean = (u: string) => (u || "").split("#")[0];

/** Previous / next page following the header menu: every link of the menu group, column by column, in order. */
export function menuSequence(menu: any[], path: string, prefer?: string): { key: string; prev: L | null; next: L | null } | null {
  // A page listed under two menus follows the menu the visitor arrived from.
  const groups = [...(menu || [])].sort((a, b) => Number(`menu:${b.label}` === prefer) - Number(`menu:${a.label}` === prefer));
  for (const m of groups) {
    const seen = new Set<string>();
    const items: L[] = [];
    (m.columns || []).forEach((c: any) => (c.links || []).forEach((l: any) => {
      const u = clean(l.url);
      if (!l.label || !u.startsWith("/") || u.includes("?") || seen.has(u)) return;
      seen.add(u); items.push({ label: l.label, to: u });
    }));
    const i = items.findIndex((x) => x.to === path);
    if (i >= 0 && items.length > 1) return { key: `menu:${m.label}`, prev: items[i - 1] || null, next: items[i + 1] || null };
  }
  return null;
}
